import React, { useState } from "react";
import { selectUser } from "../../features/userSlice";
import { useSelector } from "react-redux";
import { selectRoles } from "../../features/roleSlice";
import { Button } from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { stringToColor } from "../../utilities";
import LogoutButton from "../../auth/logout-button";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import { PageShell } from "../../components/UI/Page";

const Profile = () => {
  const user = useSelector(selectUser);
  const role = useSelector(selectRoles);
  const [pickedImage, setPickedImage] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [image, setImage] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFileInputChange = (e) => {
    var file = e.target.files[0];
    if (file) {
      var element = document.getElementById("file");
      var picked = element.files[0];
      var blob = picked.slice(0, picked.size);
      file = new File([blob], user.id, { type: `${picked.type}` });

      setImage({
        preview: URL.createObjectURL(e.target.files[0]),
        data: e.target.files[0],
      });
      setPickedImage(file);
      setUploadStatus(null);
    }
  };

  const cancelPick = () => {
    setImage(null);
    setPickedImage(null);
    setUploadStatus(null);
    const element = document.getElementById("file");
    if (element) element.value = "";
  };

  const handleUpload = async () => {
    if (!pickedImage) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", pickedImage);

    try {
      const response = await fetch("/api/profile/image/upload", {
        method: "POST",
        body: formData,
      });
      setUploadStatus(response.ok ? "success" : "failure");
    } catch (error) {
      setUploadStatus("failure");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="app-canvas min-h-[calc(100vh-140px)] w-full">
      <PageShell size="sm" className="!max-w-xl">
        <div className="app-card relative isolate overflow-hidden rounded-[1.75rem] border">
          {/* Banner — the same soft sky/violet wash as the homepage hero */}
          <div
            aria-hidden="true"
            className="relative h-28 overflow-hidden bg-gradient-to-br from-sky-400/40 via-violet-400/30 to-amber-200/30 dark:from-sky-500/30 dark:via-violet-500/25 dark:to-transparent"
          >
            <div className="absolute -right-10 -top-16 h-48 w-48 rounded-full bg-violet-400/40 blur-3xl dark:bg-violet-500/30" />
            <div className="absolute -bottom-20 left-6 h-40 w-40 rounded-full bg-sky-300/50 blur-3xl dark:bg-sky-500/25" />
          </div>

          <div className="px-6 pb-6 sm:px-8">
            {/* Avatar — the photo itself opens the picker */}
            <div className="-mt-14 flex flex-col items-center text-center">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => document.getElementById("file").click()}
                  aria-label="Change photo"
                  className="group relative block h-28 w-28 overflow-hidden rounded-full border-4 border-white bg-slate-300 shadow-lg transition hover:shadow-xl dark:border-slate-800 dark:bg-slate-700 glass:dark:border-white/10"
                >
                  <img
                    src={image ? image.preview : `/api/profile/image?id=${user?.id}`}
                    alt={user?.name || "Profile"}
                    className="h-full w-full object-cover"
                  />
                  <span className="absolute inset-0 flex items-center justify-center bg-slate-900/45 text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                    <PhotoCameraIcon />
                  </span>
                </button>
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute bottom-1 right-1 inline-flex h-9 w-9 items-center justify-center rounded-full bg-sky-600 text-white shadow-md ring-2 ring-white dark:ring-slate-800"
                >
                  <PhotoCameraIcon sx={{ fontSize: 18 }} />
                </span>
                <input
                  type="file"
                  name="file"
                  id="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileInputChange}
                />
              </div>

              <h1 className="mt-3 text-2xl font-bold tracking-tight">
                {user?.name}
              </h1>
              {user?.title && <p className="app-muted">{user.title}</p>}
              {(user?.branch || user?.email) && (
                <div className="mt-3 flex flex-wrap justify-center gap-2">
                  {user?.branch && (
                    <span className="app-chip py-1">
                      <PlaceRoundedIcon
                        sx={{ fontSize: 14 }}
                        className="text-sky-600 dark:text-sky-300"
                      />
                      {user.branch}
                    </span>
                  )}
                  {user?.email && (
                    <span className="app-chip max-w-full py-1">
                      <MailOutlineRoundedIcon
                        sx={{ fontSize: 14 }}
                        className="text-sky-600 dark:text-sky-300"
                      />
                      <span className="truncate">{user.email}</span>
                    </span>
                  )}
                </div>
              )}

              {/* Upload actions / status */}
              {pickedImage && uploadStatus !== "success" && (
                <div className="mt-4 flex items-center gap-2">
                  <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={uploading}
                  >
                    {uploading ? "Saving…" : "Save Photo"}
                  </Button>
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={cancelPick}
                    disabled={uploading}
                  >
                    Cancel
                  </Button>
                </div>
              )}
              {uploadStatus === "success" && (
                <p
                  role="status"
                  className="mt-4 rounded-xl bg-emerald-500/15 px-3 py-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-300"
                >
                  Photo updated!
                </p>
              )}
              {uploadStatus === "failure" && (
                <p
                  role="alert"
                  className="mt-4 rounded-xl bg-rose-500/10 px-3 py-1.5 text-sm font-medium text-rose-700 dark:text-rose-300"
                >
                  Upload failed. Please try again.
                </p>
              )}
            </div>

            {/* Roles */}
            <div className="app-divider mt-7 border-t pt-5">
              <p className="app-eyebrow mb-2.5">Roles</p>
              <div className="flex flex-wrap gap-2">
                {role?.roles?.length ? (
                  role.roles.map((item) => (
                    <span key={item} className="app-chip py-1 text-[0.8rem]">
                      <span
                        aria-hidden="true"
                        className="h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: stringToColor(item) }}
                      />
                      {item}
                    </span>
                  ))
                ) : (
                  <span className="app-muted text-sm">No roles assigned.</span>
                )}
              </div>
            </div>

            {/* Sign out */}
            <div className="app-divider mt-6 flex flex-col items-center gap-2 border-t pt-5">
              <LogoutButton className="app-btn app-btn-secondary w-full hover:!text-rose-600 dark:hover:!text-rose-400 sm:w-auto">
                Sign out
              </LogoutButton>
              <p className="app-muted text-xs">
                On a shared computer, sign out when you're done.
              </p>
            </div>
          </div>
        </div>
      </PageShell>
    </div>
  );
};

export default Profile;
